import { Stack, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { useMemo } from 'react';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import { RoundNameRenderer } from '../../../components/AgTable/cell-renderers/RoundRenderer';
import { ISourcingFundingRound } from '../data/sourcingProfileMockData';
import { panelSurfaceCss } from './panelStyles';
import { MoreItemsTooltip } from './MoreItemsTooltip';

const panelCss = css`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const titleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
  white-space: nowrap;
`;

const tableContentCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

/**
 * Grid layout keeps the header and row cells perfectly aligned regardless of
 * column content, and makes the Stage column elastic so long stage labels
 * (e.g. "Series A - Spendflo") still fit without truncating the pill.
 *
 * Columns: Stage | Date | Raised | Magnitude | Post-Money | Lead Investor | Investors
 */
const rowGridCss = css`
  display: grid;
  grid-template-columns: minmax(110px, 1fr) 88px 80px 72px 80px minmax(110px, 1.2fr) minmax(150px, 1.8fr);
  align-items: center;
  column-gap: 12px;
`;

const headerRowCss = css`
  padding: 0 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.66;
  color: ${colors.text.secondary};
`;

const dataRowCss = css`
  padding: 8px 12px;
  background: white;
  border: 0.5px solid ${colors.border.card};
  border-radius: 2px;
  transition: background-color 120ms ease;

  &:hover {
    background-color: rgba(86, 92, 222, 0.04);
  }
`;

const numericCellCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const dateCellCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const textCellCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Row layout for the investor columns: primary name truncates with ellipsis
 * while the `+N` overflow chip stays fully visible at the end.
 */
const investorRowCss = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const investorPrimaryCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 0 1 auto;
`;

const headerNumericCss = css`
  text-align: right;
`;

/**
 * Wrapper around `RoundNameRenderer` so the pill:
 *   - sizes to its content (padded) rather than stretching to fill the cell
 *   - is horizontally centered within the Stage column
 *
 * The shared renderer defaults to `margin: 0 auto; width: 84%`; we override
 * it to auto width so short labels like "Seed" don't leave big empty gaps
 * inside an over-wide pill. A native `title` is set by the caller to surface
 * the full stage string on hover, since sourcing data often concatenates
 * round provenance (e.g. "Seed Round - Spendflo") and can overflow.
 */
const stageCellCss = css`
  display: flex;
  justify-content: flex-start;
  min-width: 0;
  & > * {
    margin: 0 !important;
    width: auto;
    max-width: 100%;
    padding: 0 10px;
  }
`;

const magnitudeChartCss = css`
  display: flex;
  gap: 4px;
  height: 20px;
  align-items: flex-end;
`;

const magnitudeBarWrapperCss = css`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: flex-start;
  justify-content: flex-end;
`;

const BAR_HEIGHTS = [2, 4, 7, 14] as const;

interface MagnitudeChartProps {
  /** Ratio (0..1) of this round's raised amount vs the max in the dataset, or null when unknown. */
  readonly ratio: number | null;
}

/**
 * The highlighted bar maps to the round's raised amount relative to the
 * largest raise in the table (rather than the row index), so visually the
 * chart communicates scale — not order.
 */
function MagnitudeChart({ ratio }: MagnitudeChartProps) {
  const highlightedBarIndex = ratio == null ? -1 : ratioToBarIndex(ratio);
  return (
    <div css={magnitudeChartCss}>
      {BAR_HEIGHTS.map((height, i) => (
        <div key={height} css={magnitudeBarWrapperCss}>
          <div
            style={{
              height: `${height}px`,
              width: '100%',
              backgroundColor: i === highlightedBarIndex ? '#565cde' : '#eeeff1',
              borderRadius: '2px 2px 0 0',
            }}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Map a [0, 1] ratio to one of the 4 bars. We bias the lowest bucket so
 * that very small rounds still get a visible highlight instead of falling
 * off the chart.
 */
function ratioToBarIndex(ratio: number): number {
  if (ratio >= 0.75) {
    return 3;
  }
  if (ratio >= 0.4) {
    return 2;
  }
  if (ratio >= 0.15) {
    return 1;
  }
  return 0;
}

interface InvestorListProps {
  readonly label: string;
  readonly items: readonly string[];
}

/**
 * Renders the first investor as ellipsis-truncated text and surfaces the
 * remainder behind the shared `MoreItemsTooltip` chip, so the Financing
 * History investor columns stay consistent with the Digest sidebar chips.
 */
function InvestorList({ label, items }: InvestorListProps) {
  const [first, ...rest] = items;
  if (first == null) {
    return null;
  }

  return (
    <div css={investorRowCss}>
      <span css={investorPrimaryCss} title={first}>
        {first}
      </span>
      {rest.length > 0 && <MoreItemsTooltip label={label} items={rest} />}
    </div>
  );
}

interface FinancingHistoryPanelProps {
  readonly rounds: readonly ISourcingFundingRound[];
  readonly title?: string;
}

export function FinancingHistoryPanel({ rounds, title = 'Financing History' }: FinancingHistoryPanelProps) {
  // Max raised across the visible rounds, used to normalize the magnitude
  // chart. `null`/0 entries are excluded so a single unknown row doesn't
  // bias the scale.
  const maxRaised = useMemo(() => {
    let max = 0;
    for (const r of rounds) {
      if (r.raised != null && r.raised > max) {
        max = r.raised;
      }
    }
    return max;
  }, [rounds]);

  return (
    <div css={[panelSurfaceCss, panelCss]}>
      <Typography css={titleCss}>{title}</Typography>
      <div css={tableContentCss}>
        <div css={[rowGridCss, headerRowCss]}>
          <span>Stage</span>
          <span>Date</span>
          <span css={headerNumericCss}>Raised</span>
          <span>Magnitude</span>
          <span css={headerNumericCss}>Post-Money</span>
          <span>Lead Investor(s)</span>
          <span>Investor(s)</span>
        </div>
        <Stack gap={0.5}>
          {rounds.map((round) => {
            const hasRaised = round.raised != null && round.raised > 0;
            const ratio = hasRaised && maxRaised > 0 ? (round.raised as number) / maxRaised : null;

            return (
              <div key={`${round.stage}-${round.round}-${round.date}`} css={[rowGridCss, dataRowCss]}>
                <div css={stageCellCss} title={round.stage}>
                  <RoundNameRenderer name={round.stage} style={{ fontSize: '0.85rem', margin: 0 }} />
                </div>
                <span css={dateCellCss}>{round.date || '—'}</span>
                <span css={numericCellCss}>
                  {hasRaised ? FMT.format('currencyShort', round.raised as number) : '—'}
                </span>
                <MagnitudeChart ratio={ratio} />
                <span css={numericCellCss}>
                  {round.postMoney != null ? FMT.format('currencyShort', round.postMoney) : '—'}
                </span>
                <div css={textCellCss}>
                  {round.leadInvestors.length > 0 ? (
                    <InvestorList label='Lead Investor(s)' items={round.leadInvestors} />
                  ) : (
                    '—'
                  )}
                </div>
                <div css={textCellCss}>
                  {round.allInvestors.length > 0 ? (
                    <InvestorList label='Investor(s)' items={round.allInvestors} />
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            );
          })}
        </Stack>
      </div>
    </div>
  );
}
